/**
 * Local wrapper around @formkit/auto-animate that uses our app's React instance.
 *
 * The core `autoAnimate` function is pure DOM — no React dependency. The package's
 * `@formkit/auto-animate/react` hook imports React separately, which causes rspack
 * to bundle a second React instance and crash with "Cannot read properties of null
 * (reading 'useState')". This wrapper avoids that by using our own React imports.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';
import autoAnimate from '@formkit/auto-animate';
import type { AutoAnimateOptions, AutoAnimationPlugin } from '@formkit/auto-animate';

type UseAutoAnimateReturn = [(node: HTMLElement | null) => void, (enabled: boolean) => void];

export function useAutoAnimate(options?: Partial<AutoAnimateOptions> | AutoAnimationPlugin): UseAutoAnimateReturn {
  const [controller, setController] = useState<ReturnType<typeof autoAnimate>>();
  const memoizedOptions = useMemo(() => options, []);

  const element = useCallback(
    (node: HTMLElement | null) => {
      if (node instanceof HTMLElement) {
        setController(autoAnimate(node, memoizedOptions));
      } else {
        setController(undefined);
      }
    },
    [memoizedOptions],
  );

  const setEnabled = useCallback(
    (enabled: boolean) => {
      if (controller) {
        enabled ? controller.enable() : controller.disable();
      }
    },
    [controller],
  );

  useEffect(() => {
    return () => {
      controller?.destroy?.();
    };
  }, [controller]);

  return [element, setEnabled];
}
